import {
  LayoutDashboard,
  Users,
  GitBranch,
  FileText,
  PenTool,
  FileStack,
  UserCheck,
  BarChart3,
  Download,
  Building2,
  ClipboardList,
  Search,
  Activity,
  FolderOpen,
  Zap,
  Bell,
  Calculator,
  Landmark,
  ShieldCheck,
  Timer,
  ClipboardCheck,
  Lock,
  Globe,
  Code,
  Database,
  Mail,
  Settings,
  Tag,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Section, GridContainer } from "../layout";
import { AnimatedSection } from "../shared";

interface FeatureItemProps {
  icon: LucideIcon;
  label: string;
}

function FeatureItem({ icon: Icon, label }: FeatureItemProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 transition-transform duration-200 group-hover:scale-[1.05]">
      <Icon
        size={16}
        className="text-text-secondary group-hover:text-text-primary transition-colors duration-200"
      />
      <span className="text-[10px] font-medium text-text-secondary group-hover:text-text-primary text-center leading-tight transition-colors duration-200">
        {label}
      </span>
    </div>
  );
}

// 28 items organized by position in grid
const row1 = [
  { icon: LayoutDashboard, label: "Dashboard" },
  { icon: Users, label: "CRM" },
  { icon: GitBranch, label: "Pipeline" },
  { icon: FileText, label: "Contratos" },
  { icon: PenTool, label: "Firmas" },
  { icon: FileStack, label: "Templates" },
  { icon: UserCheck, label: "Inversores" },
  { icon: BarChart3, label: "Informes" },
];

const row2Left = [
  { icon: Download, label: "Exportar" },
  { icon: Building2, label: "Promotores" },
  { icon: ClipboardList, label: "Formularios" },
];
const row2Right = [
  { icon: Search, label: "Búsqueda" },
  { icon: Activity, label: "Actividad" },
  { icon: FolderOpen, label: "Documentos" },
];

const row3Left = [
  { icon: Zap, label: "Automatización" },
  { icon: Bell, label: "Alertas" },
  { icon: Calculator, label: "Calculadora" },
];
const row3Right = [
  { icon: Landmark, label: "AEAT" },
  { icon: ShieldCheck, label: "Compliance" },
  { icon: Timer, label: "Countdown" },
];

const row4 = [
  { icon: ClipboardCheck, label: "Auditoría" },
  { icon: Lock, label: "Permisos" },
  { icon: Globe, label: "Portal" },
  { icon: Code, label: "API" },
  { icon: Database, label: "Backup" },
  { icon: Mail, label: "Email" },
  { icon: Settings, label: "Ajustes" },
  { icon: Tag, label: "Etiquetas" },
];

// Mobile layout: 6 cols × 4 rows, 20 features (8 removed, 2 labels shortened)
const mobileRow1 = [
  { icon: LayoutDashboard, label: "Dash" },
  { icon: Users, label: "CRM" },
  { icon: GitBranch, label: "Pipeline" },
  { icon: FileText, label: "Contratos" },
  { icon: PenTool, label: "Firmas" },
  { icon: FileStack, label: "Templates" },
];
const mobileRow2Left = [
  { icon: UserCheck, label: "Inversores" },
  { icon: BarChart3, label: "Informes" },
];
const mobileRow2Right = [
  { icon: Download, label: "Exportar" },
  { icon: Building2, label: "Promotores" },
];
const mobileRow3Left = [
  { icon: Activity, label: "Actividad" },
  { icon: FolderOpen, label: "Docs" },
];
const mobileRow3Right = [
  { icon: Bell, label: "Alertas" },
  { icon: Calculator, label: "Calculadora" },
];
const mobileRow4 = [
  { icon: Landmark, label: "AEAT" },
  { icon: ShieldCheck, label: "Compliance" },
  { icon: Lock, label: "Permisos" },
  { icon: Globe, label: "Portal" },
  { icon: Code, label: "API" },
  { icon: ClipboardCheck, label: "Auditoría" },
];

/**
 * Features Grid section with logo in the center.
 * Uses the main GridContainer columns directly for alignment.
 * - Desktop (xl): columns 3-10 of 12-column grid
 * - Tablet (md): columns 1-8 of 8-column grid
 * - Mobile: columns 1-4 of 4-column grid
 */
export function FeaturesGrid() {
  // Feature cell with hover: border change only (scale is on content)
  const cellClass =
    "group col-span-1 aspect-square border border-border-primary -mt-px -ml-px flex items-center justify-center transition-colors duration-200 hover:border-text-primary hover:z-10 relative";

  // Empty columns (no borders, no content) - only show on desktop (xl)
  const emptyCol = "col-span-1 hidden xl:block";

  return (
    <Section id="solucion" size="md" noPaddingTop className="relative z-10 pb-20 md:pb-16 xl:pb-20">
      <AnimatedSection>
        <GridContainer>
          {/* Header text above grid */}
          <div className="col-span-4 md:col-span-8 xl:col-span-12 text-center mb-8">
            <span className="text-accent text-sm font-medium tracking-wide uppercase">
              THE SOLUTION
            </span>
            <h2 className="mt-4 text-2xl md:text-3xl xl:text-4xl font-semibold text-text-primary tracking-tight">
              <span className="md:hidden">Una plataforma. Control total.</span>
              <span className="hidden md:inline">Una plataforma. Un sistema. Control total.</span>
            </h2>
            <p className="text-2xl md:text-3xl xl:text-4xl font-semibold text-text-subtitle tracking-tight">
              Hulahoop Pro.
            </p>
            <p className="mt-6 text-body text-text-secondary">
              <span className="block">
                El incentivo fiscal no necesita más herramientas. Necesita estructura.
              </span>
              <span className="block">
                Todo lo que hoy gestionas en múltiples sistemas vive aquí, conectado.
              </span>
            </p>
          </div>

          {/* Mobile grid - 6 cols × 4 rows, 20 features */}
          <div className="col-span-4 md:hidden relative">
            <div className="grid grid-cols-6 pt-px pl-px">
              {/* ROW 1: 6 items */}
              {mobileRow1.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}

              {/* ROW 2: 2 items, logo (2×2), 2 items */}
              {mobileRow2Left.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}
              <div className="col-span-2 row-span-2 aspect-square border border-border-primary -mt-px -ml-px flex items-center justify-center relative">
                <img src="/hula-logomark.png" alt="Hulahoop" className="relative z-10 w-12 h-12" />
              </div>
              {mobileRow2Right.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}

              {/* ROW 3: 2 items, (logo continues), 2 items */}
              {mobileRow3Left.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}
              {mobileRow3Right.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}

              {/* ROW 4: 6 items */}
              {mobileRow4.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}
            </div>
          </div>

          {/* Grid - 12 columns to match page grid system */}
          <div className="col-span-4 md:col-span-8 xl:col-span-12 relative hidden md:block">
            {/* Left fade mask - starts at column 3 (after 2 empty cols = 16.666%) */}
            <div
              className="absolute top-0 bottom-0 w-16 pointer-events-none z-20 hidden xl:block"
              style={{
                left: "calc(16.666% - 1px)",
                background: "linear-gradient(to right, white 0%, transparent 100%)",
              }}
            />
            {/* Right fade mask - ends at column 10 (before 2 empty cols = 83.333%) */}
            <div
              className="absolute top-0 bottom-0 w-16 pointer-events-none z-20 hidden xl:block"
              style={{
                right: "calc(16.666% - 1px)",
                background: "linear-gradient(to left, white 0%, transparent 100%)",
              }}
            />
            <div className="grid grid-cols-8 xl:grid-cols-12 pt-px pl-px">
              {/* ROW 1: empty cols 1-2, content cols 3-10, empty cols 11-12 */}
              <div className={emptyCol} />
              <div className={emptyCol} />
              {row1.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}
              <div className={emptyCol} />
              <div className={emptyCol} />

              {/* ROW 2: empty, 3 items, logo (2x2), 3 items, empty */}
              <div className={emptyCol} />
              <div className={emptyCol} />
              {row2Left.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}
              {/* Logo area - 2x2 */}
              <div className="col-span-2 row-span-2 aspect-square border border-border-primary -mt-px -ml-px flex items-center justify-center relative">
                <img src="/hula-logomark.png" alt="Hulahoop" className="relative z-10 w-16 h-16" />
              </div>
              {row2Right.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}
              <div className={emptyCol} />
              <div className={emptyCol} />

              {/* ROW 3: empty, 3 items, (logo continues), 3 items, empty */}
              <div className={emptyCol} />
              <div className={emptyCol} />
              {row3Left.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}
              {/* Logo continues via row-span-2 */}
              {row3Right.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}
              <div className={emptyCol} />
              <div className={emptyCol} />

              {/* ROW 4: empty, 8 items, empty */}
              <div className={emptyCol} />
              <div className={emptyCol} />
              {row4.map((f) => (
                <div key={f.label} className={cellClass}>
                  <FeatureItem icon={f.icon} label={f.label} />
                </div>
              ))}
              <div className={emptyCol} />
              <div className={emptyCol} />
            </div>
          </div>
        </GridContainer>
      </AnimatedSection>
    </Section>
  );
}
